import { useMemo } from "react";
import { useAppState } from "@/store/useApp";
import { getDocumentMetadata, getSectionMetadata } from "../utils/readingTimeUtils";
import { ReadingTimeDisplay } from "./ReadingTimeDisplay";
import { TableOfContentNode } from "../side-panel/toc/tocService";

interface ReadingTimeProps {
  layout?: "horizontal" | "vertical";
  className?: string;
  currentSectionId?: string | null;
  tableOfContents?: TableOfContentNode[];
}

export const ReadingTime = ({ 
  layout = "vertical", 
  className,
  currentSectionId,
  tableOfContents = [],
}: ReadingTimeProps) => {
  const document = useAppState((state) => state.currentDocument);

  // Calculate reading time metadata
  const readingTimeMetadata = useMemo(() => {
    if (!document) return null;

    // Always get total document metadata
    const documentMetadata = getDocumentMetadata(document);

    // If we have a current section, also calculate section metadata
    let sectionWordCount: number | undefined;
    let sectionTitle: string | undefined;

    if (currentSectionId && tableOfContents.length > 0) {
      const sectionMetadata = getSectionMetadata(currentSectionId, tableOfContents, document);
      sectionWordCount = sectionMetadata.wordCount;
      sectionTitle = sectionMetadata.sectionTitle;
    }

    return {
      totalWordCount: documentMetadata.wordCount,
      estimatedTime: documentMetadata.estimatedTime,
      sectionWordCount,
      sectionTitle,
    };
  }, [document, currentSectionId, tableOfContents]);

  if (!readingTimeMetadata) return null;

  return (
    <ReadingTimeDisplay
      totalWordCount={readingTimeMetadata.totalWordCount}
      estimatedTime={readingTimeMetadata.estimatedTime}
      sectionWordCount={readingTimeMetadata.sectionWordCount}
      layout={layout}
      className={className}
    />
  );
};
